import React, { FC, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';
import { productImageUrl } from '~Utils/images';
import Icon from '~ComponentsImproved/Icon';
import { EposMenuCategory } from '../../../types';

/** 128px rail, less its 10px padding and the button's own 6px, either side. */
const THUMB_IMAGE_WIDTH = 96;

/**
 * Image on top, label underneath. The selected state is applied by toggling
 * the `is-active` class straight on the node (see Menu/index.tsx) so the
 * scroll-spy never triggers a React render. The touch state therefore rides on
 * a data attribute - if React owned `className` it would wipe `is-active` on
 * the re-render every touch causes.
 */
const RailButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  font-family: var(--eposFontFamily);
  color: var(--eposAccent);
  padding: 8px 6px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.18s ease-out, color 0.18s ease-out;

  &[data-touching] {
    transform: scale(0.96);
  }

  &.is-active {
    background: var(--eposButton);
    color: var(--eposButtonText);
  }
`;

/**
 * The category photo. Without one it becomes the "category tile" from the
 * theme: category background showing the backoffice icon in category text if
 * the category has one, otherwise the name - like the online ordering site,
 * so the label under the thumb is hidden in that last case.
 */
const Thumb = styled.span<{ imageUrl: string }>`
  width: 100%;
  /** Matches the 3:2 crop the CDN is asked for, so nothing is re-cropped. */
  aspect-ratio: 3 / 2;
  border-radius: 16px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  box-sizing: border-box;
  text-align: center;
  font-size: var(--eposCategoryLabelSize);
  font-weight: 700;
  line-height: 1.2;
  background-color: var(--eposCategoryBackground);
  color: var(--eposCategoryText);
  background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : 'none')};
  background-size: cover;
  background-position: center;
`;

const Label = styled.span`
  font-size: var(--eposCategoryLabelSize);
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  letter-spacing: -0.2px;
`;

interface ButtonProps {
  category: EposMenuCategory;
  onSelect: (categoryId: string) => void;
}

/**
 * Memoized so the rail only ever renders once per menu - note that useButton
 * lives in here, not in the parent's map, so a touch re-renders one button.
 */
const CategoryButton: FC<ButtonProps> = memo(({ category, onSelect }) => {
  const handleClick = useCallback(() => onSelect(category.id), [category.id, onSelect]);
  // `touching`/`disabledToast` are styling props, not DOM attributes - keep them
  // off the real <button> so React doesn't warn about unknown attributes.
  const { touching, disabledToast, ...button } = useButton({ onClick: handleClick });

  const imageUrl = productImageUrl(category.imageUrl, THUMB_IMAGE_WIDTH);
  /** Photo first; the icon only stands in when there is none. */
  const iconSrc = imageUrl ? undefined : category.iconSrc;
  const showLabelBelow = !!imageUrl || !!iconSrc;

  return (
    <RailButton {...button} data-category-id={category.id} data-touching={touching || undefined}>
      <Thumb imageUrl={imageUrl}>
        {iconSrc ? (
          <Icon src={iconSrc} color="var(--eposCategoryText)" width="60%" height="60%" center />
        ) : (
          !imageUrl && category.name
        )}
      </Thumb>
      {showLabelBelow && <Label>{category.name}</Label>}
    </RailButton>
  );
});

CategoryButton.displayName = 'CategoryButton';

export default CategoryButton;
